import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError, formatAR, parseAR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateField } from "@/components/DatePickers";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Save, Plus, Trash2, BookOpen, Upload, Download } from "lucide-react";

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const TIPOS_CONCEPTO = [
  ["remunerativo", "Remunerativo"],
  ["nr_solo", "No remunerativo solamente"],
  ["nr_os_sind", "No rem. base de obra social y sindicato"],
  ["nr_os", "No rem. sólo base de obra social"],
  ["nr_sind", "No rem. sólo base de sindicato"],
];
const vigKey = (v) => `${v.anio}-${v.mes}`;
const vigLabel = (v) => `${MESES[v.mes - 1]} ${v.anio}`;

function GuiaSueldos() {
  const G = [
    {
      t: "1. Escalas salariales",
      why: "Acá viven los básicos de convenio y los acuerdos no remunerativos (suma fija) de Comercio (CCT 130/75), por categoría y por mes, tal como los publica la circular de FAECYS. Sin esta tabla, la calculadora no sabría cuánto vale el básico de cada categoría en cada mes. Ya cargamos la circular Abril–Julio 2026 completa (47 categorías, incluidos los menores de 6 y 8 horas).",
      how: [
        "Elegí la Vigencia (mes/año) en el desplegable para ver y editar esa escala.",
        "Cada fila tiene 5 valores: Básico, AUM NO REM (ACU 06 y 12/2025), AUM NO REM (ACU 04/2026), SUMA FIJA (no remunerativo puro, opcional) y el Total, que se calcula solo sumando los cuatro.",
        "Arriba de la tabla está el Nombre Suma Fija: es uno por vigencia y es el texto exacto que sale en el recibo (ej: “Asig Extraord Única Vez”). La Suma Fija se proporciona por días trabajados y jornada reducida (igual que el Básico) y no integra ninguna base de aportes ni contribuciones.",
        "Editá cualquier celda y tocá Guardar para grabar toda la escala del mes.",
        "Con + Agregar categoría sumás una fila nueva; con el tachito la eliminás.",
        "Cuando salga una circular nueva: elegí mes y año en Nueva vigencia y tocá Crear (copia actual) — se copia la escala en pantalla y después ajustás los valores nuevos.",
        "Abajo están las Marcas del acuerdo no remunerativo: tres llaves que indican si lo no remunerativo es base de Presentismo, de Antigüedad y/o de Sindicato. Las marcas son POR VIGENCIA: cada mes/año tiene las suyas (cambian al cambiar la vigencia seleccionada) y se copian automáticamente al crear una nueva vigencia. La calculadora usa las marcas del mes que se liquida.",
      ],
      where: "Cuando el usuario liquide un sueldo de Comercio, la calculadora busca el Básico y el Acuerdo NR según la categoría del legajo y el mes de liquidación. Si un mes no tiene escala cargada, se usa automáticamente la del mes inmediato anterior. Las marcas definen si sobre lo no remunerativo se calculan también la antigüedad, el presentismo y los aportes sindicales. Que lo no remunerativo sea base de obra social no se define acá: depende de la marca “Afiliado a OSECAC” de cada empleado.",
    },
    {
      t: "2. Adicionales",
      why: "Son valores de Comercio que la circular publica por fuera de la tabla de básicos y también cambian mes a mes: Adicional Cajeros (A, B y C), Adicional por Vidrieras (Art. 23), INACAP y La Estrella. Por eso tienen su propia pantalla con vigencia.",
      how: [
        "Elegí la Vigencia para ver los valores de ese mes.",
        "Editá los importes y tocá Guardar.",
        "Para un mes nuevo: completá mes y año en Nueva vigencia y tocá Crear — se graba con los valores que estén en pantalla.",
        "OJO: INACAP no viene en la circular, quedó en $0. Cargalo cuando tengas el valor vigente.",
      ],
      where: "En la futura calculadora de sueldos: si el empleado es cajero, se suma el Adicional Cajero de su letra (el valor de escala dividido 12); si arma vidrieras, se suma el Adicional Vidrieras. INACAP (valor fijo) y La Estrella (% sobre el total remunerativo) se agregan como contribuciones patronales cuando el convenio es Comercio.",
    },
    {
      t: "3. Feriados",
      why: "La ley trata distinto al feriado trabajado y al no trabajado. Para no obligar al usuario a acordarse qué feriados caen en el mes, mantenemos acá el calendario oficial de feriados de Argentina (2026 ya está cargado).",
      how: [
        "Para agregar uno: elegí la fecha en el calendario, escribí el nombre y tocá Agregar.",
        "Para borrar uno, usá el tachito de la fila.",
      ],
      where: "Al liquidar, la calculadora muestra los feriados del mes elegido y pregunta por cada uno: ¿se trabajó? Sí/No. Con esa respuesta genera los renglones “Feriado trabajado” o “Feriado no trabajado” en las remuneraciones, con sus fórmulas propias (la del no trabajado usa el divisor 25).",
    },
    {
      t: "4. Parámetros",
      why: "Concentra los porcentajes y valores fijos propios de la liquidación mensual de sueldos, para que no queden escritos “a mano” dentro del cálculo y se puedan actualizar sin tocar nada más.",
      how: [
        "Editá el valor que necesites y tocá Guardar. Aplica a los cálculos que se hagan de ahí en adelante.",
      ],
      where: "Cada campo pega en un lugar puntual del cálculo: Asistencia (8,33%) es el presentismo sobre básico + antigüedad; Antigüedad Comercio (1% por año) multiplica los años del empleado; S.E.C. Art. 100 (2%) y F.A.E.C.yS. (0,5%) son las retenciones sindicales obligatorias de Comercio. Importante: las bases mínimas y máximas de seguridad social, las alícuotas de aportes/contribuciones, la Detracción Dto. 814 y los valores fijos de Seguro de Vida (SCVO) y A.R.T. por empleado NO están acá — se toman de la pestaña Parámetros general, compartida con la calculadora de Aguinaldo (se cargan una sola vez y quedan versionadas). Los fijos de SCVO y ART se suman al costo del empleador cuando la liquidación no es 1ª quincena.",
    },
    {
      t: "5. ABM Conceptos",
      why: "Es el catálogo de conceptos que el usuario va a poder agregar a mano en el sector Remuneraciones (horas extra, premios, etc.). Definirlos acá evita que cada usuario escriba cualquier cosa y, sobre todo, deja claro cómo tributa cada concepto.",
      how: [
        "Para crear uno: escribí el nombre, elegí el tipo y tocá Agregar.",
        "Para modificar uno existente: editá el nombre o el tipo en la fila y tocá el ícono de guardar.",
        "Para borrarlo, el tachito.",
      ],
      where: "Estos conceptos aparecen como sugerencias al agregar renglones en la calculadora. El tipo define adónde va la plata: Remunerativo suma al total remunerativo (paga todos los aportes); No remunerativo solamente no genera aportes; y las tres variantes de no remunerativo con base indican si ese importe entra en la base de Obra Social, de Sindicato, o de ambas.",
    },
  ];
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="sueldos-guia-btn" className="rounded-none gap-2"><BookOpen size={15} /> Guía de uso</Button>
      </DialogTrigger>
      <DialogContent data-testid="sueldos-guia-dialog" className="rounded-none max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg">Guía — Sección Sueldos del panel admin</DialogTitle>
        </DialogHeader>
        <div className="space-y-6 text-sm leading-relaxed">
          <p className="text-muted-foreground">
            Esta sección carga los <b>datos de referencia</b> que va a usar la nueva <b>Calculadora de Sueldos (Recibo de Sueldos)</b>: escalas, adicionales, feriados, parámetros y conceptos. Todo lo que definas acá lo toma la calculadora automáticamente — el usuario final no tiene que cargar nada de esto. La calculadora en sí se habilita en las próximas etapas del desarrollo.
          </p>
          {G.map((s) => (
            <div key={s.t} className="border border-border p-4 space-y-3">
              <p className="font-semibold">{s.t}</p>
              <div>
                <p className="label-tiny mb-1">¿Por qué existe?</p>
                <p>{s.why}</p>
              </div>
              <div>
                <p className="label-tiny mb-1">¿Cómo se usa?</p>
                <ul className="list-disc pl-5 space-y-1">{s.how.map((h, i) => <li key={i}>{h}</li>)}</ul>
              </div>
              <div>
                <p className="label-tiny mb-1">¿Dónde impacta?</p>
                <p>{s.where}</p>
              </div>
            </div>
          ))}
          <p className="text-xs text-muted-foreground">
            Regla general: todo se guarda con fecha de vigencia (mes/año). La calculadora siempre usa la vigencia del mes de liquidación y, si ese mes no está cargado, cae en la vigencia anterior más cercana.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Panel({ title, children, actions }) {
  return (
    <div className="bg-white border border-border">
      <div className="p-5 border-b border-border flex items-center justify-between flex-wrap gap-2">
        <p className="label-tiny">{title}</p>{actions}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function NumInput({ value, onChange, testid, className = "" }) {
  return (
    <Input data-testid={testid} className={`rounded-none text-right ${className}`} value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={() => { const n = parseAR(value); onChange(n ? formatAR(n) : value === "" ? "" : formatAR(0)); }} />
  );
}

export default function SueldosTab() {
  return (
    <Tabs defaultValue="escalas">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <TabsList className="rounded-none bg-white border border-border p-0 h-auto flex-wrap">
          {[["escalas", "Escalas salariales"], ["adicionales", "Adicionales"], ["feriados", "Feriados"], ["sparams", "Parámetros"], ["conceptos", "ABM Conceptos"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`sueldos-subtab-${v}`} className="rounded-none data-[state=active]:bg-slate-900 data-[state=active]:text-white text-slate-600 px-4 py-2">{l}</TabsTrigger>
          ))}
        </TabsList>
        <GuiaSueldos />
      </div>
      <div className="mt-5 space-y-6">
        <TabsContent value="escalas"><EscalasSection /></TabsContent>
        <TabsContent value="adicionales"><AdicionalesSection /></TabsContent>
        <TabsContent value="feriados"><FeriadosSection /></TabsContent>
        <TabsContent value="sparams"><ParametrosSection /></TabsContent>
        <TabsContent value="conceptos"><ConceptosSection /></TabsContent>
      </div>
    </Tabs>
  );
}

function ImportExcel({ tipo, onDone }) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [dec, setDec] = useState({});
  const [busy, setBusy] = useState(false);
  const nombre = tipo === "escalas" ? "escala salarial" : "adicionales";

  const descargar = async () => {
    try {
      const r = await api.get(`/admin/sueldos/${tipo}/plantilla`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `plantilla_${tipo}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo descargar la plantilla"); }
  };
  const onFile = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post(`/admin/sueldos/${tipo}/importar/preview`, fd);
      setPreview(r.data); setDec({});
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const confirmar = async () => {
    try {
      const meses = preview.meses.map((m) => (tipo === "escalas"
        ? { mes: m.mes, anio: m.anio, reemplazar: !!dec[`${m.mes}-${m.anio}`], rows: m.rows, nombreSumaFija: m.nombreSumaFija || "" }
        : { mes: m.mes, anio: m.anio, reemplazar: !!dec[`${m.mes}-${m.anio}`], ...m.valores }));
      const r = await api.post(`/admin/sueldos/${tipo}/importar/confirmar`, { meses });
      const imp = r.data.importados || [], omi = r.data.omitidos || [];
      toast.success(imp.length ? `Importado: ${imp.join(", ")}` : "No se importó ningún mes");
      if (omi.length) toast.warning(`Omitidos (ya existían, sin reemplazar): ${omi.join(", ")}`, { duration: 7000 });
      setOpen(false); setPreview(null);
      onDone && onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <>
      <Button variant="outline" onClick={descargar} data-testid={`${tipo}-plantilla-btn`} className="rounded-none gap-1"><Download size={14} /> Descargar plantilla</Button>
      <Button variant="outline" onClick={() => { setPreview(null); setOpen(true); }} data-testid={`${tipo}-import-btn`} className="rounded-none gap-1"><Upload size={14} /> Importar Excel</Button>
      <Dialog open={open} onOpenChange={(o) => { if (!o) { setOpen(false); setPreview(null); } }}>
        <DialogContent className="rounded-none max-w-2xl max-h-[85vh] overflow-y-auto" data-testid={`${tipo}-import-dialog`}>
          <DialogHeader><DialogTitle>Importar {nombre} desde Excel</DialogTitle></DialogHeader>
          {!preview ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                Subí el archivo <b>.xlsx</b> basado en la plantilla. Cada fila indica su <b>Mes</b> y <b>Año</b> (desplegables en el Excel): podés cargar <b>varios meses en un mismo archivo</b>. Nada se guarda hasta que confirmes la vista previa.
              </p>
              <input type="file" accept=".xlsx" data-testid={`${tipo}-import-file`} onChange={(e) => onFile(e.target.files?.[0])}
                className="block w-full text-sm border border-border p-3 cursor-pointer" />
              {busy && <p className="text-xs text-slate-500">Leyendo archivo…</p>}
            </div>
          ) : (
            <div className="space-y-3">
              {(preview.errores || []).map((er, i) => (
                <p key={i} className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-1.5" data-testid={`${tipo}-import-error-${i}`}>✖ {er}</p>
              ))}
              {(preview.advertencias || []).map((w, i) => (
                <p key={i} className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5">⚠ {w}</p>
              ))}
              {(preview.meses || []).map((m) => {
                const key = `${m.mes}-${m.anio}`;
                return (
                  <div key={key} className="border border-border p-3" data-testid={`${tipo}-import-mes-${m.mes}-${m.anio}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <p className="text-sm font-medium">
                        {m.mesNombre} {m.anio} — {m.estado === "nueva"
                          ? <span className="text-emerald-700">✚ NUEVA vigencia: se creará</span>
                          : <span className="text-amber-700">⚠ YA EXISTE en el sistema</span>}
                      </p>
                      {m.estado === "existente" && (
                        <label className="flex items-center gap-2 text-xs cursor-pointer">
                          <input type="checkbox" data-testid={`${tipo}-import-replace-${m.mes}-${m.anio}`} checked={!!dec[key]} onChange={(e) => setDec({ ...dec, [key]: e.target.checked })} />
                          Reemplazar los valores actuales
                        </label>
                      )}
                    </div>
                    {(m.cambios || []).length > 0 && (
                      <ul className="text-xs text-slate-600 mt-1.5 space-y-0.5">
                        {m.cambios.slice(0, 8).map((c, i) => <li key={i}>· {c}</li>)}
                        {m.cambios.length > 8 && <li>… y {m.cambios.length - 8} cambios más</li>}
                      </ul>
                    )}
                    {(m.advertencias || []).map((w, i) => <p key={i} className="text-xs text-amber-700 mt-1">⚠ {w}</p>)}
                    {tipo === "escalas" && m.nombreSumaFija && <p className="text-xs text-slate-600 mt-1" data-testid={`escalas-import-nombresf-${m.mes}-${m.anio}`}>Nombre Suma Fija: “{m.nombreSumaFija}”</p>}
                    {tipo === "escalas" && <p className="text-xs text-muted-foreground mt-1">{(m.rows || []).length} categorías</p>}
                  </div>
                );
              })}
              {(preview.meses || []).length === 0 && <p className="text-sm text-slate-600">El archivo no tiene filas de datos.</p>}
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" className="rounded-none" data-testid={`${tipo}-import-back`} onClick={() => setPreview(null)}>Volver</Button>
                <Button className="rounded-none brand-bg text-white" data-testid={`${tipo}-import-confirm`}
                  disabled={(preview.errores || []).length > 0 || (preview.meses || []).length === 0} onClick={confirmar}>
                  Confirmar e importar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function EscalasSection() {
  const [vigencias, setVigencias] = useState([]);
  const [sel, setSel] = useState(null);
  const [rows, setRows] = useState([]);
  const [flags, setFlags] = useState(null);
  const [nombreSF, setNombreSF] = useState("");
  const [newVig, setNewVig] = useState({ mes: "", anio: "" });

  const loadVigencias = async (keep) => {
    const r = await api.get("/admin/sueldos/escalas/vigencias");
    setVigencias(r.data);
    if (r.data.length) {
      const found = keep && r.data.find((v) => vigKey(v) === vigKey(keep));
      setSel(found || r.data[r.data.length - 1]);
    }
  };
  useEffect(() => {
    loadVigencias().catch(() => {});
  }, []);
  useEffect(() => {
    if (!sel) return;
    api.get(`/admin/sueldos/escalas?mes=${sel.mes}&anio=${sel.anio}`)
      .then((r) => setRows(r.data.map((x) => ({ categoria: x.categoria, basico: formatAR(x.basico), nr1: formatAR(x.nr1), nr2: formatAR(x.nr2), sumaFija: x.sumaFija ? formatAR(x.sumaFija) : "" }))))
      .catch(() => {});
    api.get(`/admin/sueldos/flags?mes=${sel.mes}&anio=${sel.anio}`).then((r) => { setFlags(r.data); setNombreSF(r.data.nombreSumaFija || ""); }).catch(() => {});
  }, [sel]);

  const save = async () => {
    try {
      await api.put("/admin/sueldos/escalas/bulk", {
        mes: sel.mes, anio: sel.anio,
        rows: rows.map((r) => ({ categoria: r.categoria, basico: parseAR(r.basico), nr1: parseAR(r.nr1), nr2: parseAR(r.nr2), sumaFija: parseAR(r.sumaFija) })),
      });
      if (flags) {
        await api.put("/admin/sueldos/flags", {
          mes: sel.mes, anio: sel.anio,
          nrBasePresentismo: flags.nrBasePresentismo, nrBaseAntiguedad: flags.nrBaseAntiguedad, nrBaseSindicato: flags.nrBaseSindicato,
          nombreSumaFija: nombreSF.trim(),
        });
      }
      toast.success("Escala guardada");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveFlags = async (next) => {
    setFlags(next);
    try {
      await api.put("/admin/sueldos/flags", {
        mes: sel.mes, anio: sel.anio,
        nrBasePresentismo: next.nrBasePresentismo, nrBaseAntiguedad: next.nrBaseAntiguedad, nrBaseSindicato: next.nrBaseSindicato,
        nombreSumaFija: nombreSF.trim(),
      });
      toast.success(`Marcas actualizadas para ${vigLabel(sel)}`);
      setFlags({ ...next, vigencia: { mes: sel.mes, anio: sel.anio } });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const createVigencia = async () => {
    const mes = parseInt(newVig.mes, 10), anio = parseInt(newVig.anio, 10);
    if (!mes || !anio) { toast.error("Indicá mes y año de la nueva vigencia"); return; }
    try {
      await api.post("/admin/sueldos/escalas/vigencia", { mes, anio, copyMes: sel?.mes, copyAnio: sel?.anio });
      toast.success("Vigencia creada (copiada de la actual)");
      setNewVig({ mes: "", anio: "" });
      await loadVigencias({ mes, anio });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setCell = (i, k, v) => setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  return (
    <div className="space-y-6">
      <Panel title="Escalas salariales — Comercio (CCT 130/75)"
        actions={<div className="flex flex-wrap gap-2">
          <ImportExcel tipo="escalas" onDone={() => loadVigencias(sel).catch(() => {})} />
          <Button onClick={save} disabled={!sel} data-testid="escalas-save" className="rounded-none brand-bg text-white gap-2"><Save size={15} /> Guardar</Button>
        </div>}>
        <div className="flex flex-wrap items-end gap-4 mb-4">
          <div>
            <Label className="label-tiny">Vigencia</Label>
            <Select value={sel ? vigKey(sel) : ""} onValueChange={(k) => setSel(vigencias.find((v) => vigKey(v) === k))}>
              <SelectTrigger data-testid="escalas-vigencia" className="mt-1.5 rounded-none w-44"><SelectValue placeholder="Elegir mes" /></SelectTrigger>
              <SelectContent>{vigencias.map((v) => <SelectItem key={vigKey(v)} value={vigKey(v)}>{vigLabel(v)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-2">
            <div>
              <Label className="label-tiny">Nueva vigencia</Label>
              <div className="flex gap-2 mt-1.5">
                <Select value={newVig.mes} onValueChange={(m) => setNewVig({ ...newVig, mes: m })}>
                  <SelectTrigger data-testid="escalas-newvig-mes" className="rounded-none w-36"><SelectValue placeholder="Mes" /></SelectTrigger>
                  <SelectContent>{MESES.map((n, i) => <SelectItem key={i} value={String(i + 1)}>{n}</SelectItem>)}</SelectContent>
                </Select>
                <Input data-testid="escalas-newvig-anio" className="rounded-none w-24" placeholder="Año" value={newVig.anio} onChange={(e) => setNewVig({ ...newVig, anio: e.target.value })} />
              </div>
            </div>
            <Button variant="outline" onClick={createVigencia} data-testid="escalas-newvig-crear" className="rounded-none gap-1"><Plus size={14} /> Crear (copia actual)</Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mb-3">Si un mes de liquidación no tiene escala definida, se usa la del mes inmediato anterior.</p>
        <div className="mb-4 max-w-md">
          <Label className="label-tiny">Nombre Suma Fija (uno por vigencia — así sale en el recibo)</Label>
          <Input data-testid="escala-nombre-sumafija" className="mt-1.5 rounded-none" placeholder="Ej: Asig Extraord Única Vez"
            value={nombreSF} onChange={(e) => setNombreSF(e.target.value)} />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="escalas-table">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="p-2 label-tiny">Categoría</th>
                <th className="p-2 label-tiny text-right">Básico</th>
                <th className="p-2 label-tiny text-right">AUM NO REM<br />(ACU 06 y 12/2025)</th>
                <th className="p-2 label-tiny text-right">AUM NO REM<br />(ACU 04/2026)</th>
                <th className="p-2 label-tiny text-right">Suma Fija</th>
                <th className="p-2 label-tiny text-right">Total</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className="border-b border-border/60">
                  <td className="p-1.5 min-w-[280px]"><Input data-testid={`escala-cat-${i}`} className="rounded-none" value={row.categoria} onChange={(e) => setCell(i, "categoria", e.target.value)} /></td>
                  <td className="p-1.5 w-36"><NumInput testid={`escala-basico-${i}`} value={row.basico} onChange={(v) => setCell(i, "basico", v)} /></td>
                  <td className="p-1.5 w-36"><NumInput testid={`escala-nr1-${i}`} value={row.nr1} onChange={(v) => setCell(i, "nr1", v)} /></td>
                  <td className="p-1.5 w-36"><NumInput testid={`escala-nr2-${i}`} value={row.nr2} onChange={(v) => setCell(i, "nr2", v)} /></td>
                  <td className="p-1.5 w-36"><NumInput testid={`escala-sumafija-${i}`} value={row.sumaFija} onChange={(v) => setCell(i, "sumaFija", v)} /></td>
                  <td className="p-1.5 w-36 text-right font-medium" data-testid={`escala-total-${i}`}>{formatAR(parseAR(row.basico) + parseAR(row.nr1) + parseAR(row.nr2) + parseAR(row.sumaFija))}</td>
                  <td className="p-1.5"><button type="button" data-testid={`escala-del-${i}`} onClick={() => setRows(rows.filter((_, idx) => idx !== i))} className="text-destructive hover:opacity-70"><Trash2 size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Button variant="outline" data-testid="escala-add-row" className="rounded-none mt-3 gap-1"
          onClick={() => setRows([...rows, { categoria: "", basico: "", nr1: "", nr2: "", sumaFija: "" }])}><Plus size={14} /> Agregar categoría</Button>
        <p className="text-xs text-muted-foreground mt-3" data-testid="sumafija-nota">
          <b>Suma Fija:</b> no remunerativo puro — se proporciona por días trabajados y jornada reducida, igual que el Básico. No genera presentismo, antigüedad, sindicato, zona ni obra social, y no integra ninguna base de aportes ni contribuciones. Si es 0, no aparece en el recibo. Al crear una nueva vigencia se copia (como los no remunerativos).
        </p>
      </Panel>

      {flags && sel && (
        <Panel title={`Marcas del acuerdo no remunerativo — vigencia ${vigLabel(sel)}`}>
          <p className="text-xs text-muted-foreground mb-3" data-testid="flags-vigencia-nota">
            Estas marcas aplican a la vigencia seleccionada ({vigLabel(sel)}). Cada mes tiene sus propias marcas y se copian al crear una nueva vigencia.
            {flags.vigencia && (flags.vigencia.mes !== sel.mes || flags.vigencia.anio !== sel.anio) ? ` Mostrando las de ${flags.vigencia.mes}/${flags.vigencia.anio} (vigencia anterior más cercana): al tocar una llave se guardan para ${vigLabel(sel)}.` : ""}
          </p>
          <div className="space-y-3">
            {[["nrBasePresentismo", "Lo no remunerativo es base de Presentismo (asistencia)"],
              ["nrBaseAntiguedad", "Lo no remunerativo es base de Antigüedad"],
              ["nrBaseSindicato", "Lo no remunerativo es base de Sindicato"]].map(([k, l]) => (
              <div key={k} className="flex items-center justify-between border border-border p-3">
                <span className="text-sm">{l}</span>
                <Switch data-testid={`flag-${k}`} checked={flags[k]} onCheckedChange={(c) => saveFlags({ ...flags, [k]: c })} />
              </div>
            ))}
            <p className="text-xs text-muted-foreground">La base de obra social sobre lo no remunerativo depende de la marca &quot;Afiliado a OSECAC&quot; de cada empleado.</p>
          </div>
        </Panel>
      )}
    </div>
  );
}

function AdicionalesSection() {
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const [f, setF] = useState(null);
  const [newVig, setNewVig] = useState({ mes: "", anio: "" });

  const load = async (keep) => {
    const r = await api.get("/admin/sueldos/adicionales");
    setItems(r.data);
    if (r.data.length) {
      const found = keep && r.data.find((v) => vigKey(v) === vigKey(keep));
      setSel(found || r.data[r.data.length - 1]);
    }
  };
  useEffect(() => { load().catch(() => {}); }, []);
  useEffect(() => {
    if (!sel) return;
    setF({ cajeroA: formatAR(sel.cajeroA), cajeroB: formatAR(sel.cajeroB), cajeroC: formatAR(sel.cajeroC), vidrieras: formatAR(sel.vidrieras), inacapFijo: formatAR(sel.inacapFijo), laEstrellaPct: formatAR(sel.laEstrellaPct) });
  }, [sel]);

  const save = async (mes = sel.mes, anio = sel.anio) => {
    try {
      await api.put("/admin/sueldos/adicionales", {
        mes, anio,
        cajeroA: parseAR(f.cajeroA), cajeroB: parseAR(f.cajeroB), cajeroC: parseAR(f.cajeroC),
        vidrieras: parseAR(f.vidrieras), inacapFijo: parseAR(f.inacapFijo), laEstrellaPct: parseAR(f.laEstrellaPct),
      });
      toast.success("Adicionales guardados");
      await load({ mes, anio });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const createVigencia = async () => {
    const mes = parseInt(newVig.mes, 10), anio = parseInt(newVig.anio, 10);
    if (!mes || !anio) { toast.error("Indicá mes y año"); return; }
    await save(mes, anio);
    setNewVig({ mes: "", anio: "" });
  };

  return (
    <Panel title="Adicionales — Comercio (valores con vigencia)"
      actions={<div className="flex flex-wrap gap-2">
        <ImportExcel tipo="adicionales" onDone={() => load(sel).catch(() => {})} />
        <Button onClick={() => save()} disabled={!sel || !f} data-testid="adicionales-save" className="rounded-none brand-bg text-white gap-2"><Save size={15} /> Guardar</Button>
      </div>}>
      <div className="flex flex-wrap items-end gap-4 mb-5">
        <div>
          <Label className="label-tiny">Vigencia</Label>
          <Select value={sel ? vigKey(sel) : ""} onValueChange={(k) => setSel(items.find((v) => vigKey(v) === k))}>
            <SelectTrigger data-testid="adicionales-vigencia" className="mt-1.5 rounded-none w-44"><SelectValue placeholder="Elegir mes" /></SelectTrigger>
            <SelectContent>{items.map((v) => <SelectItem key={vigKey(v)} value={vigKey(v)}>{vigLabel(v)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label className="label-tiny">Nueva vigencia (con los valores en pantalla)</Label>
            <div className="flex gap-2 mt-1.5">
              <Select value={newVig.mes} onValueChange={(m) => setNewVig({ ...newVig, mes: m })}>
                <SelectTrigger data-testid="adicionales-newvig-mes" className="rounded-none w-36"><SelectValue placeholder="Mes" /></SelectTrigger>
                <SelectContent>{MESES.map((n, i) => <SelectItem key={i} value={String(i + 1)}>{n}</SelectItem>)}</SelectContent>
              </Select>
              <Input data-testid="adicionales-newvig-anio" className="rounded-none w-24" placeholder="Año" value={newVig.anio} onChange={(e) => setNewVig({ ...newVig, anio: e.target.value })} />
            </div>
          </div>
          <Button variant="outline" onClick={createVigencia} disabled={!f} data-testid="adicionales-newvig-crear" className="rounded-none gap-1"><Plus size={14} /> Crear</Button>
        </div>
      </div>
      {f && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-3xl">
          {[["cajeroA", "Adicional Cajero A ($)"], ["cajeroB", "Adicional Cajero B ($)"], ["cajeroC", "Adicional Cajero C ($)"],
            ["vidrieras", "Adicional Vidrieras ($ — Art. 23)"], ["inacapFijo", "INACAP (valor fijo $)"], ["laEstrellaPct", "La Estrella (%)"]].map(([k, l]) => (
            <div key={k}><Label className="label-tiny">{l}</Label><NumInput testid={`adicional-${k}`} className="mt-1.5" value={f[k]} onChange={(v) => setF({ ...f, [k]: v })} /></div>
          ))}
        </div>
      )}
      <p className="text-xs text-muted-foreground mt-4">INACAP no figura en la circular: cargalo cuando tengas el valor vigente. La Estrella se aplica como % del total remunerativo.</p>
    </Panel>
  );
}

function FeriadosSection() {
  const [items, setItems] = useState([]);
  const [nuevo, setNuevo] = useState({ fecha: "", nombre: "" });

  const load = () => api.get("/admin/sueldos/feriados").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!nuevo.fecha || !nuevo.nombre.trim()) { toast.error("Completá fecha y nombre"); return; }
    try {
      await api.post("/admin/sueldos/feriados", nuevo);
      toast.success("Feriado agregado");
      setNuevo({ fecha: "", nombre: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    try { await api.delete(`/admin/sueldos/feriados/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const fmtFecha = (iso) => { const [y, m, d] = String(iso).split("-"); return `${d}/${m}/${y}`; };

  return (
    <Panel title="Feriados de Argentina">
      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div><Label className="label-tiny">Fecha</Label><div className="mt-1.5"><DateField iso testid="feriado-fecha" value={nuevo.fecha} onChange={(v) => setNuevo({ ...nuevo, fecha: v })} /></div></div>
        <div className="flex-1 min-w-[220px]"><Label className="label-tiny">Nombre del feriado</Label><Input data-testid="feriado-nombre" className="mt-1.5 rounded-none" value={nuevo.nombre} onChange={(e) => setNuevo({ ...nuevo, nombre: e.target.value })} /></div>
        <Button onClick={add} data-testid="feriado-add" className="rounded-none brand-bg text-white gap-1"><Plus size={14} /> Agregar</Button>
      </div>
      <table className="w-full text-sm" data-testid="feriados-table">
        <thead><tr className="border-b border-border text-left"><th className="p-2 label-tiny w-32">Fecha</th><th className="p-2 label-tiny">Feriado</th><th className="p-2 w-10"></th></tr></thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.id} className="border-b border-border/60">
              <td className="p-2">{fmtFecha(it.fecha)}</td>
              <td className="p-2">{it.nombre}</td>
              <td className="p-2"><button type="button" data-testid={`feriado-del-${it.id}`} onClick={() => del(it.id)} className="text-destructive hover:opacity-70"><Trash2 size={14} /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs text-muted-foreground mt-3">La calculadora propone los feriados del mes de liquidación para indicar si se trabajó o no.</p>
    </Panel>
  );
}

function ParametrosSection() {
  const [f, setF] = useState(null);
  useEffect(() => {
    api.get("/admin/sueldos/parametros").then((r) => setF({
      asistenciaPct: formatAR(r.data.asistenciaPct), antiguedadComercioPct: formatAR(r.data.antiguedadComercioPct),
      secArt100Pct: formatAR(r.data.secArt100Pct), faecysArt100Pct: formatAR(r.data.faecysArt100Pct),
      notas: r.data.notas || "",
    })).catch(() => {});
  }, []);
  if (!f) return null;
  const save = async () => {
    try {
      await api.put("/admin/sueldos/parametros", {
        asistenciaPct: parseAR(f.asistenciaPct), antiguedadComercioPct: parseAR(f.antiguedadComercioPct),
        secArt100Pct: parseAR(f.secArt100Pct), faecysArt100Pct: parseAR(f.faecysArt100Pct),
        notas: f.notas,
      });
      toast.success("Parámetros guardados");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Panel title="Parámetros de la Calculadora de Sueldos"
      actions={<Button onClick={save} data-testid="sparams-save" className="rounded-none brand-bg text-white gap-2"><Save size={15} /> Guardar</Button>}>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-3xl">
        {[["asistenciaPct", "Asistencia (%)"], ["antiguedadComercioPct", "Antigüedad Comercio (% por año)"],
          ["secArt100Pct", "S.E.C. Art. 100 CCT 130/75 (%)"], ["faecysArt100Pct", "F.A.E.C.yS. Art. 100 (%)"]].map(([k, l]) => (
          <div key={k}><Label className="label-tiny">{l}</Label><NumInput testid={`sparam-${k}`} className="mt-1.5" value={f[k]} onChange={(v) => setF({ ...f, [k]: v })} /></div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mt-4" data-testid="sparams-nota">
        Las bases mínimas y máximas de seguridad social, las alícuotas de aportes/contribuciones, la <b>Detracción Dto. 814</b> y los valores fijos de <b>Seguro de Vida (SCVO)</b> y <b>A.R.T. por empleado</b> se toman de la pestaña <b>Parámetros</b> (compartida con la calculadora de Aguinaldo).
      </p>
    </Panel>
  );
}

function ConceptosSection() {
  const [items, setItems] = useState([]);
  const [nuevo, setNuevo] = useState({ nombre: "", tipo: "remunerativo" });

  const load = () => api.get("/admin/sueldos/conceptos").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!nuevo.nombre.trim()) { toast.error("Ingresá el nombre del concepto"); return; }
    try { await api.post("/admin/sueldos/conceptos", nuevo); toast.success("Concepto agregado"); setNuevo({ nombre: "", tipo: "remunerativo" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const update = async (it) => {
    try { await api.put(`/admin/sueldos/conceptos/${it.id}`, { nombre: it.nombre, tipo: it.tipo }); toast.success("Concepto actualizado"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    try { await api.delete(`/admin/sueldos/conceptos/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setItem = (id, patch) => setItems((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  return (
    <Panel title="ABM de conceptos del sector Remuneraciones">
      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div className="flex-1 min-w-[220px]"><Label className="label-tiny">Nombre</Label><Input data-testid="concepto-nombre" className="mt-1.5 rounded-none" value={nuevo.nombre} onChange={(e) => setNuevo({ ...nuevo, nombre: e.target.value })} /></div>
        <div>
          <Label className="label-tiny">Tipo</Label>
          <Select value={nuevo.tipo} onValueChange={(t) => setNuevo({ ...nuevo, tipo: t })}>
            <SelectTrigger data-testid="concepto-tipo" className="mt-1.5 rounded-none w-72"><SelectValue /></SelectTrigger>
            <SelectContent>{TIPOS_CONCEPTO.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={add} data-testid="concepto-add" className="rounded-none brand-bg text-white gap-1"><Plus size={14} /> Agregar</Button>
      </div>
      <table className="w-full text-sm" data-testid="conceptos-table">
        <thead><tr className="border-b border-border text-left"><th className="p-2 label-tiny">Nombre</th><th className="p-2 label-tiny">Tipo</th><th className="p-2 w-20"></th></tr></thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.id} className="border-b border-border/60">
              <td className="p-1.5"><Input data-testid={`concepto-nombre-${it.id}`} className="rounded-none" value={it.nombre} onChange={(e) => setItem(it.id, { nombre: e.target.value })} /></td>
              <td className="p-1.5 w-80">
                <Select value={it.tipo} onValueChange={(t) => setItem(it.id, { tipo: t })}>
                  <SelectTrigger data-testid={`concepto-tipo-${it.id}`} className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>{TIPOS_CONCEPTO.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </td>
              <td className="p-1.5">
                <div className="flex gap-2">
                  <button type="button" data-testid={`concepto-save-${it.id}`} onClick={() => update(it)} className="text-slate-600 hover:brand-text"><Save size={14} /></button>
                  <button type="button" data-testid={`concepto-del-${it.id}`} onClick={() => del(it.id)} className="text-destructive hover:opacity-70"><Trash2 size={14} /></button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs text-muted-foreground mt-3">Estos conceptos aparecen como sugerencias al agregar líneas en el sector Remuneraciones de la calculadora.</p>
    </Panel>
  );
}
